import React, { useEffect, useState } from "react"
import "./partsearch.css"
import { Alert, Col, Container, Row, UncontrolledTooltip } from "reactstrap"
import "flatpickr/dist/themes/material_blue.css"
import { withTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import SearchResults from "./SearchResults"
import PartRfqModal from "./PartRfqModal"
import { useLoading } from "helpers/custom_hooks"
import {
  GetParts,
  SearchDigikeyParts,
  GetDigikeyQuota,
} from "helpers/backend_helper"
import { displayMessage, IsTraderCompany } from "helpers/misc_helpers"

const Dashboard = props => {
  const setLoading = useLoading()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [parts, setParts] = useState([])
  const [error, setError] = useState()
  const [showResults, setShowResults] = useState(false)
  const [rfqOpen, setRfqOpen] = useState(false)
  const [rfqPrefill, setRfqPrefill] = useState(null)

  const openRfq = prefill => {
    setRfqPrefill(prefill || { partNumber: searchInput || search || "" })
    setRfqOpen(true)
  }
  const search = searchParams.get("search")
  const showTraderCheckbox = !IsTraderCompany()
  const [searchInput, setSearchInput] = useState(search ?? "")
  const [quota, setQuota] = useState(null)
  // Optionen als lokaler State — sie lösen KEINE Suche aus, sondern werden erst
  // bei der nächsten Suche (Enter) angewendet.
  const [includeTraderArticles, setIncludeTraderArticles] = useState(true) // default an
  const [advancedSearch, setAdvancedSearch] = useState(false) // default aus

  const loadQuota = () => {
    GetDigikeyQuota()
      .then(q => setQuota(q))
      .catch(() => {})
  }

  const handleSearch = e => {
    e.preventDefault()
    const term = searchInput.trim()
    if (!term) return
    // URL aktualisieren (für Teilen/Deeplink) und direkt suchen — so wird auch
    // bei gleichem Suchbegriff mit geänderten Optionen neu gesucht.
    setSearchParams(
      prev => {
        const p = new URLSearchParams(prev)
        p.set("search", term)
        return p
      },
      { replace: true }
    )
    searchParts(term, includeTraderArticles, advancedSearch)
  }

  // Externe (DigiKey-)Treffer in das Zeilenformat der Ergebnistabelle überführen
  const mapExternalResults = results =>
    (results ?? []).map((row, idx) => ({
      ...row,
      id: `ext-${idx}`,
      name: row.supplier || "DigiKey",
      region: "External",
      isExternal: true,
    }))

  const searchParts = async (searchTerm, withTrader, advanced) => {
    setLoading(true)
    try {
      // Interne chipsconnect-Suche immer; externe (DigiKey-)Suche nur bei
      // aktivierter "Advanced search". Ein Fehler der externen Suche darf die
      // eigenen Ergebnisse nie blockieren.
      const tasks = [GetParts(searchTerm, withTrader)]
      if (advanced)
        tasks.push(SearchDigikeyParts(encodeURIComponent(searchTerm)))

      const results = await Promise.allSettled(tasks)

      const ownRes = results[0]
      if (ownRes.status === "rejected") throw ownRes.reason
      const ownParts = ownRes.value ?? []

      let externalParts = []
      if (advanced) {
        const extRes = results[1]
        externalParts =
          extRes.status === "fulfilled"
            ? mapExternalResults(extRes.value)
            : []
        loadQuota() // verbrauchtes Kontingent nach externer Suche aktualisieren
      }

      setParts([...ownParts, ...externalParts])
      setShowResults(true)
    } catch (error) {
      if (error?.response?.status == 429)
        displayMessage(
          "You exceeded your monthly limit of searches. Please upgrade to premium license!",
          setError
        )
      else displayMessage("An unexpected error happened!", setError)
    }
    setLoading(false)
  }

  useEffect(() => {
    loadQuota()
  }, [])

  // Nur beim ersten Laden suchen, wenn ein Suchbegriff via URL kommt (Deeplink).
  // Danach wird ausschließlich per Enter (handleSearch) gesucht.
  useEffect(() => {
    if (!search) {
      setShowResults(false)
      return
    }
    searchParts(search, includeTraderArticles, advancedSearch)
  }, [])

  return (
    <React.Fragment>
      <section
        className="section hero-section bg-ico-hero"
        id="home"
        style={{
          paddingTop: showResults ? "120px" : "150px",
          paddingBottom: showResults ? "50px" : "100px",
        }}
      >
        <div className="bg-overlay bg-primary"></div>
        <Container>
          <Row className="align-items-center">
            <Col lg={5}>
              <div className="text-white-50">
                <h3 className="text-white fw-semibold mb-3">Search Part</h3>
                <form
                  className="app-search d-none d-lg-block"
                  onSubmit={handleSearch}
                >
                  <div className="d-flex gap-2">
                    <div className="position-relative flex-grow-1">
                      <input
                        type="text"
                        name="search"
                        className="form-control"
                        placeholder='Search „Manufacturer Part Number"...'
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                      />
                      <span
                        className="bx bx-search-alt"
                        onClick={handleSearch}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                    <button type="submit" className="btn btn-primary px-4">
                      <i className="bx bx-search me-1" />
                      Search
                    </button>
                  </div>
                </form>

                {showTraderCheckbox && (
                  <div className="form-check form-check-primary mt-1 ms-3">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="includeTraderArticles"
                      checked={includeTraderArticles}
                      onChange={e => setIncludeTraderArticles(e.target.checked)}
                    />
                    <label
                      className="form-check-label text-white-50"
                      htmlFor="includeTraderArticles"
                    >
                      Include Trader articles
                    </label>
                  </div>
                )}

                <div className="form-check form-check-primary mt-1 ms-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="advancedSearch"
                    checked={advancedSearch}
                    onChange={e => setAdvancedSearch(e.target.checked)}
                  />
                  <label
                    className="form-check-label text-white-50"
                    htmlFor="advancedSearch"
                  >
                    Advanced search{" "}
                    <span
                      id="advancedSearchInfo"
                      style={{
                        cursor: "help",
                        textDecoration: "underline dotted",
                        fontSize: 12,
                      }}
                    >
                      What is it?
                    </span>
                  </label>
                  <UncontrolledTooltip
                    placement="top"
                    target="advancedSearchInfo"
                  >
                    Advanced search also queries external distributors beyond
                    chipsconnect stock. Limited per month.
                  </UncontrolledTooltip>
                </div>
                {quota && quota.limit > 0 && (
                  <div
                    className="text-white-50 ms-3 mt-1"
                    style={{ fontSize: 12 }}
                  >
                    {quota.remaining} of {quota.limit} advanced searches left
                    this month
                    {quota.resetDate
                      ? ` · resets ${new Date(
                          quota.resetDate
                        ).toLocaleDateString()}`
                      : ""}
                  </div>
                )}

                {showResults && (
                  <div className="mt-4">
                    <p className="text-white-50 mb-2">
                      Can&apos;t find a matching part? Send a Part RFQ and
                      chipsconnect sources it for you.
                    </p>
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={() => openRfq()}
                    >
                      <i className="bx bx-mail-send me-1" />
                      Part RFQ
                    </button>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      {error && (
        <Alert className="mt-4" color="danger">
          {error}
        </Alert>
      )}

      {showResults && (
        <SearchResults
          parts={parts}
          search={search}
          includeTraderArticles={
            showTraderCheckbox ? includeTraderArticles : true
          }
          onPartRfq={row =>
            openRfq({
              partNumber: row.partNumber,
              manufacturer: row.manufacturer,
              quantity: row.quantity,
            })
          }
        />
      )}

      <PartRfqModal
        isOpen={rfqOpen}
        toggle={() => setRfqOpen(false)}
        prefill={rfqPrefill}
        onSuccess={() => navigate("/requests")}
      />
    </React.Fragment>
  )
}

export default withTranslation()(Dashboard)
